import type React from 'react';
import type { RefObject } from 'react';

import { Image } from '@change/ds-core-ui';

import { useWatermarkedImage } from './hooks/useWatermarkedImage';
import type { WatermarkPlacement } from './hooks/useWatermarkedImage/utils';

export type { WatermarkPlacement } from './hooks/useWatermarkedImage/utils';

/**
 * Renders an image with a baked-in watermark. While the watermark is being composited the
 * image is withheld (an empty box preserves the layout); if compositing fails the original
 * unmarked image is shown instead and `onCompositeError` is called.
 */
export const WatermarkedImage: React.FC<Props> = ({ id, src, placement, measureRef, onReady, onCompositeError }) => {
	const {
		data: { status, watermarkedUrl },
	} = useWatermarkedImage({ src, placement, measureRef, onCompositeError });

	if (status === 'pending') {
		return <div className="aspect-video w-full" data-qa="watermarked-image-pending" />;
	}

	if (status === 'error' || !watermarkedUrl) {
		// a failed composite shows the unmarked original; useWatermarkedImage has already reported
		// it via onCompositeError, so the failure rate stays measurable
		return <Image aspectRatio="16:9" id={id} src={src} alt="" className="w-full align-top" />;
	}

	return <Image aspectRatio="16:9" id={id} src={watermarkedUrl} alt="" className="w-full align-top" onLoad={onReady} />;
};

type Props = Readonly<{
	id: string;
	src: string;
	placement: WatermarkPlacement;
	/**
	 * Element whose layout width is the image's rendered width, read at composite time to
	 * convert the placement's CSS-px padding into image px.
	 */
	measureRef: RefObject<HTMLElement | null>;
	/** Called when the watermarked image has painted. */
	onReady?: () => void;
	/** Called when compositing failed and the unmarked original is shown instead. */
	onCompositeError?: () => void;
}>;
